import formatDate from 'date-fns/format';
import { QlfConfirmationDialog, useQlfTranslation } from '@qualifio/ui-commons';
import { TemplateVersionAttributes } from '@loyalty/shared-common/template-api-client';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useScheduleTemplate } from '../../hooks/use-templates';

interface ReplaceScheduledVersionDialogProps {
  open: boolean;
  existingScheduledVersion: TemplateVersionAttributes | null;
  newTemplateId: string;
  newPublicationDate: Date;
  onClose: () => void;
  onConfirm: () => void;
}

export function ReplaceScheduledVersionDialog({
  open,
  existingScheduledVersion,
  newTemplateId,
  newPublicationDate,
  onClose,
  onConfirm,
}: ReplaceScheduledVersionDialogProps) {
  const { t } = useTranslation();
  const { t: htmlT } = useQlfTranslation('translation');
  const { mutateAsync: scheduleTemplate } = useScheduleTemplate();

  const handleConfirm = useCallback(async () => {
    await scheduleTemplate({ templateId: newTemplateId, publicationDate: newPublicationDate });
    onConfirm();
    onClose();
  }, [scheduleTemplate, newTemplateId, newPublicationDate, onConfirm, onClose]);

  const versionReference = useMemo(() => {
    if (existingScheduledVersion?.name) {
      return t('editor.template.version.reference.withName', {
        name: existingScheduledVersion.name,
      });
    }
    return t('editor.template.version.reference.aVersion');
  }, [existingScheduledVersion?.name, t]);

  const { formattedDate, formattedTime } = useMemo(() => {
    if (!existingScheduledVersion?.scheduledPublicationDate) {
      return { formattedDate: '', formattedTime: '' };
    }
    const date = new Date(existingScheduledVersion.scheduledPublicationDate);
    return {
      formattedDate: formatDate(date, 'dd/MM/yyyy'),
      formattedTime: formatDate(date, 'HH:mm a'),
    };
  }, [existingScheduledVersion?.scheduledPublicationDate]);

  if (!existingScheduledVersion?.scheduledPublicationDate) {
    return null;
  }

  return (
    <QlfConfirmationDialog
      title={t('editor.template.version.schedule.replace.title')}
      content={htmlT('editor.template.version.schedule.replace.message', {
        versionReference,
        date: formattedDate,
        time: formattedTime,
      })}
      confirmButtonText={t('editor.template.version.schedule.replace.confirm')}
      cancelButtonText={t('generic.actions.cancel')}
      onConfirm={handleConfirm}
      onClose={onClose}
      color="warning"
      confirmButtonColor="secondary"
      open={open}
    />
  );
}
